import { Add, MoreVert } from '@mui/icons-material';
import { Autocomplete, Box, Button, CircularProgress, IconButton, Popover, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Actions, Thunks } from '../../actions';
import { BLUEPRINT_ORGANIZATION_NAME, DEVICE_PROFILE_REGEX } from '../../constants';
import { useDeviceMaster } from '../../data/DataProviders/DeviceMasterProvider';
import { DeviceProfileChannelsProvider } from '../../data/DataProviders/DeviceProfileChannelsProvider';
import { useToasts } from '../../data/DataProviders/ToastProvider';
import { pushAppRoute } from '../../data/domain/route-manager';
import { isRecordFound, useSelectedOrganizationData } from '../../hooks/data-hooks';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { sortList } from '../../lib/manage-alarms-helpers';
import { Selectors } from '../../selectors';
import { DerivedChannelData, DeviceProfileData, isDataLoaded } from '../../types';
import { MasterDataKey } from '../../types/device';
import { useAuthHelper } from '../AuthContextProvider';
import { DataIndicators } from '../DataIndicators';
import { NavigationPromptModal } from '../NavigationPromptModal/NavigationPromptModal';
import { ChannelList } from './ChannelList';
import { DerivedChannelModal } from './DerivedChannelModal';

export const ReferenceProfile: React.FC = () => {
    const { t } = useLanguageLocale();
    const selectedOrganization = useSelectedOrganizationData();
    const orgId = isRecordFound(selectedOrganization) ? selectedOrganization.value.id : '';
    const [deviceProfiles, setDeviceProfiles] = useState<any[]>([]);
    const [derivedChannels, setDerivedChannels] = useState<any[]>([]);
    const [existingChannels, setExistingChannels] = useState<[]>([]);
    const [profileId, setProfileId] = useState<''>('');
    const [profileName, setProfileName] = useState<string>('');
    const [preProfileName, setPreProfileName] = useState<string>('');
    const [openDerivedModal, setOpenDerivedModal] = React.useState(false);
    const history = useHistory();
    const deviceMasterData = useDeviceMaster();
    const [isDirty, setIsDirty] = useState(false);
    const dispatch = useDispatch();
    const authHelper = useAuthHelper();
    const { addToast } = useToasts();
    const manageProfileState = useSelector(Selectors.ManageDeviceProfileSelectors.manageDeviceProfiles);
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => setAnchorEl(event.currentTarget);
    const handleClose = (): void => setAnchorEl(null);
    const [isCheckAll, setIsCheckAll] = useState(true);

    const handleCloseDerivedModal = (): void => {
        setOpenDerivedModal(false);
    };
    const handleProfileChange = (value: any): void => {
        if (value && value.profileId) {
            setProfileId(value.profileId);
            setPreProfileName(value.profileName);
            setProfileName('');
        }
    };

    const handleSelectAll = (): void => {
        setIsCheckAll(true);
        handleClose();
    };
    const handleUnSelectAll = (): void => {
        setIsCheckAll(false);
        handleClose();
    };

    const onChangeDeviceProfileName = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if (e.target.value === '' || DEVICE_PROFILE_REGEX.test(e.target.value)) {
            setProfileName(e.target.value);
        }
    };

    const derivedChannelDetails = (derivedChannel: DerivedChannelData): void => {
        derivedChannels.push(derivedChannel);
        setDerivedChannels(derivedChannels);
    };

    const existingChannelsData = (_existingChannels: []): void => {
        setExistingChannels(_existingChannels);
    };

    const derivedChannelsData = (_derivedChannels: []): void => {
        setDerivedChannels(_derivedChannels);
    };

    useEffect(() => {
        if (!manageProfileState.loading && manageProfileState.success) {
            deviceMasterData.getDevicesInfo(BLUEPRINT_ORGANIZATION_NAME, orgId);
            setProfileId(profileId);
            setPreProfileName(preProfileName);
            setProfileName('');
            addToast('Device Profile Added Successfully.');
            dispatch(Actions.ManageDeviceProfilesActions.DeviceProfileSaveReset());
        }
    }, [manageProfileState.loading, manageProfileState.success]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        dispatch(Actions.ManageDeviceProfilesActions.DeviceProfileSaveUnmounted());
        deviceMasterData.getDeviceTypeMasterData(BLUEPRINT_ORGANIZATION_NAME, MasterDataKey.Starfish);
    }, []);

    useEffect(() => {
        if (
            isDataLoaded(deviceMasterData.deviceTypeData) &&
            deviceMasterData.deviceTypeData.value &&
            deviceMasterData.deviceTypeData.value.length > 0 &&
            deviceMasterData.rowKey === MasterDataKey.Starfish
        ) {
            deviceMasterData.getDevicesInfo(BLUEPRINT_ORGANIZATION_NAME, orgId);
            const childDeviceInfo = deviceMasterData.deviceTypeData.value;
            if (childDeviceInfo) {
                sortList(childDeviceInfo, 'key');
                setDeviceProfiles(childDeviceInfo);
            }
        }
    }, [deviceMasterData]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (
            isDataLoaded(deviceMasterData.deviceInfo) &&
            deviceMasterData.deviceInfo.value &&
            deviceMasterData.deviceInfo.value.length > 0 &&
            deviceMasterData.rowKey === orgId
        ) {
            const childDeviceInfo = deviceMasterData.deviceInfo.value;
            if (childDeviceInfo) {
                const orgProfiles = JSON.parse(JSON.stringify(childDeviceInfo));
                const allDeviceProfiles = orgProfiles.concat(deviceProfiles);
                sortList(allDeviceProfiles, 'key');
                setDeviceProfiles(allDeviceProfiles);
            }
        }
    }, [deviceMasterData]); //eslint-disable-line react-hooks/exhaustive-deps

    const navigateToManageDeviceProfile = (): void => {
        pushAppRoute(history, { type: 'ManageDeviceProfiles', orgId: orgId });
    };

    const onAddProfile = (): void => {
        setIsDirty(false);
        const deviceProfileData: DeviceProfileData = {
            parentDeviceProfileId: profileId,
            profileName: `${preProfileName}_${profileName}`,
            displayName: profileName,
            newChannels: derivedChannels.filter((channel) => channel.checked),
            existingChannels: existingChannels,
        };

        dispatch(Thunks.ManageDeviceProfilesThunks.SaveDeviceProfile(deviceProfileData, orgId, authHelper, addToast));
    };

    return (
        <>
            {isDataLoaded(deviceMasterData.deviceInfo) && deviceProfiles.length > 0 && (
                <Box>
                    <Box display="flex" mt={1}>
                        <Box style={{ width: '70%' }}>
                            {' '}
                            <Autocomplete
                                id="managedeviceprofile"
                                freeSolo
                                disableClearable
                                style={{ width: '100%' }}
                                options={deviceProfiles}
                                // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
                                getOptionLabel={(option) => option.key}
                                // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
                                getOptionDisabled={(option) => option.value.disabled === true}
                                selectOnFocus
                                onChange={(e, option): void => {
                                    handleProfileChange(option.value);
                                }}
                                renderInput={(params): JSX.Element => (
                                    <TextField
                                        {...params}
                                        label={t('MANAGEDEVICEPROFILES.SEARCH_PROFILE_PLACEHOLDER')}
                                        InputProps={{
                                            ...params.InputProps,
                                            type: 'search',
                                        }}
                                    />
                                )}
                            />
                        </Box>
                        <Box style={{ width: '100%', paddingLeft: 10 }}>
                            <TextField
                                inputProps={{ 'data-testid': 'profileprefix' }}
                                disabled
                                style={{ width: 400 }}
                                variant="filled"
                                id="PrfolieNamePre"
                                label="Profile Name Prefix"
                                value={preProfileName}
                                onChange={(): void => {
                                    // onChangeDeviceName(e);
                                }}
                            />
                        </Box>
                        <Box style={{ width: '100%', paddingLeft: 0 }}>
                            <TextField
                                inputProps={{ 'data-testid': 'device-name' }}
                                variant="filled"
                                required
                                style={{ width: 400 }}
                                id="PrfolieName"
                                label="Device Profile Name"
                                value={profileName}
                                onChange={(e): void => {
                                    onChangeDeviceProfileName(e);
                                }}
                            />
                        </Box>
                    </Box>

                    {profileId !== '' && (
                        <Box sx={{ mt: 5 }} display="flex" justifyContent="space-between">
                            <div>
                                <Typography
                                    color="primary"
                                    variant="subtitle1"
                                    display="inline"
                                    style={{ lineHeight: 1.5, fontWeight: 600 }}
                                >
                                    Channels
                                </Typography>

                                <Typography
                                    variant="body2"
                                    display="block"
                                    style={{ lineHeight: 1.25, fontWeight: 400 }}
                                >
                                    Select the channels that you want to add to this profile.
                                </Typography>
                            </div>
                            <Box>
                                <Button
                                    style={{ marginRight: 10 }}
                                    variant="contained"
                                    color="primary"
                                    data-testid="add-device-profile"
                                    onClick={(): void => setOpenDerivedModal(true)}
                                >
                                    <Add fontSize="medium" style={{ paddingRight: 0 }} />
                                    {t('MANAGEDEVICEPROFILES.ADD_DERIVED_CHANNEL')}
                                </Button>
                                <IconButton aria-label="Selection" onClick={handleClick} size="medium">
                                    {<MoreVert />}
                                </IconButton>
                            </Box>
                        </Box>
                    )}
                    <Box sx={{ mt: 5, maxHeight: 300, overflow: 'auto' }}>
                        {orgId && profileId !== '' && (
                            <DeviceProfileChannelsProvider orgId={orgId} deviceprofileId={profileId}>
                                <ChannelList
                                    existingChannelsData={existingChannelsData}
                                    isCheckAll={isCheckAll}
                                    derivedChannels={derivedChannels}
                                    derivedChannelsData={derivedChannelsData}
                                />
                            </DeviceProfileChannelsProvider>
                        )}
                    </Box>

                    {profileId !== '' && (
                        <Box sx={{ mt: 2 }}>
                            <Button
                                style={{ float: 'right', margin: 8 }}
                                variant="contained"
                                color="primary"
                                disabled={
                                    profileName === '' || (existingChannels.length || derivedChannels.length) === 0
                                }
                                data-testid="export-button"
                                onClick={onAddProfile}
                                startIcon={
                                    manageProfileState.loading ? (
                                        <CircularProgress size="1rem" style={{ color: 'white' }} />
                                    ) : (
                                        <></>
                                    )
                                }
                            >
                                Add Profile
                            </Button>
                            <Button
                                style={{ float: 'right', margin: 8 }}
                                variant="outlined"
                                color="primary"
                                data-testid="close-model-button"
                                onClick={navigateToManageDeviceProfile}
                            >
                                Cancel
                            </Button>
                        </Box>
                    )}
                </Box>
            )}
            {!isDataLoaded(deviceMasterData.deviceInfo) && (
                <DataIndicators
                    errorMessage="Device Profiles not found"
                    emptyMessage="Device Profiles not found"
                    inline
                    data={deviceMasterData.deviceInfo}
                    emptyIcon="Devices"
                    isEmpty={false}
                    isDetailedInfo={false}
                />
            )}

            {
                <DerivedChannelModal
                    open={openDerivedModal}
                    onClose={handleCloseDerivedModal}
                    derivedChannelData={derivedChannelDetails}
                    derivedChannels={derivedChannels}
                />
            }
            <NavigationPromptModal isDirty={isDirty} />
            <Popover
                id="selectionPopover"
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <Button sx={{ display: 'block', p: 2 }} variant="text" onClick={handleSelectAll}>
                    Select All
                </Button>
                <Button sx={{ display: 'block', p: 2 }} variant="text" onClick={handleUnSelectAll}>
                    Unselect All
                </Button>
            </Popover>
        </>
    );
};
